import type { Messages } from './en';

/**
 * Fase D (cel): temps, calendari, tifons, terratrèmols i les trucades de la mare.
 * Les línies de pràctica d'anglès es queden en anglès expressament.
 */
export const caSky: Pick<Messages, 'sky'> = {
  sky: {
    mapNames: {},
    zones: {},
    objects: {
      sky_calendar: 'Calendari',
      sky_kit: 'Kit d’emergència',
      sky_umbrellas: 'Paraigües',
    },
    npc: {},
    items: {},

    kinds: {
      sunny: 'Assolellat',
      cloudy: 'Ennuvolat',
      rain: 'Pluja',
      storm: 'Tempesta',
      cool: 'Front fred',
      typhoon: 'Tifó',
    },
    hud: {
      /** Text emergent de la caixa de l'hora. */
      title: (week: number, date: string, kind: string) => `Setmana ${week} · ${date} · ${kind}`,
    },

    forecast: {
      title: 'Previsió del temps',
      today: 'Avui',
      tomorrow: 'Demà',
      rain: (p: number) => `☂ ${p}%`,
      /** Substitueix la línia fixa del temps del teletip de notícies per la previsió real d'avui. */
      tickerToday: (kind: string, lo: number, hi: number, p: number) => `Temps: ${kind}, ${lo}–${hi}°C, probabilitat de pluja ${p}% · Recorda: comprova la font abans de compartir ·`,
      range: (lo: number, hi: number) => `${lo}–${hi}°C`,
      showers: (from: string, to: string) => `Ruixats ${from}–${to}`,
      alert: '🌀 Alerta de tifó! Demà és dia lliure per tifó: no hi ha escola ni feina. Queda’t a casa!',
      ticker: '🌀 ALERTA DE TIFÓ · Avisos marítims i terrestres activats · Demà: dia lliure per tifó · Guarda aigua i carrega el mòbil ·',
      tips: {
        sunny: 'Calor i sol: beu aigua i posa’t una gorra.',
        cloudy: 'Cel ennuvolat, potser cau un ruixat.',
        rain: 'Dia de pluja: agafa el paraigua!',
        storm: 'Tempestes a la tarda: porta paraigua i allunya’t dels arbres alts.',
        cool: 'Ha arribat un front fred: posa’t una jaqueta.',
        typhoon: 'Tifó! Queda’t a casa i lluny de les finestres.',
      },
    },

    toasts: {
      typhoonEve: '🌀 Alerta de tifó: demà és dia lliure per tifó (颱風假)! Prepara’t a casa.',
      typhoonDay: '🌀 Dia lliure per tifó! L’escola i les botigues són tancades. Queda’t segur a casa.',
      typhoonPassed: '🌤️ El tifó ja ha passat. Les portes tornen a ser obertes.',
      doorClosed: 'Tancat pel tifó. Queda’t segur a casa!',
      tooWindy: 'El vent bufa massa fort. No és segur sortir amb un tifó!',
      rainStarts: '🌧️ Comença a ploure!',
      stormStarts: '⛈️ Trons! S’acosta una tempesta.',
      soaked: '💦 T’has xopat amb la pluja! Compra un paraigua a la botiga.',
      umbrellaOpen: '☂️ Obres el paraigua. Ben sequet!',
      sentHome: '🌀 Dia lliure per tifó: avui no hi ha escola. Et quedes a casa.',
      windy: '🍃 El vent del tifó udola a fora!',
      quakeAlert: '📳 Alerta sísmica (地震速報)! Aviat tremolarà fort: protegeix-te el cap!',
    },

    calendar: {
      who: 'Calendari',
      today: (date: string, kind: string, temp: number, pct: number) => `Avui és **${date}**. ${kind}, ${temp}°C, probabilitat de pluja ${pct}%.`,
      tomorrow: (kind: string, lo: number, hi: number, pct: number) => `Demà: ${kind}, ${lo}–${hi}°C, pluja ${pct}%.`,
      tear: 'Arrenques el full d’ahir. A moltes cases taiwaneses pengen calendaris de fulls com aquest.',
      practice: 'Digues-ho en anglès',
      // Línies de pràctica d'anglès.
      sayDate: (date: string) => `Today is ${date}.`,
      say: {
        sunny: 'It’s sunny and hot today.',
        cloudy: 'It’s cloudy today.',
        rain: 'It’s rainy today. I need an umbrella.',
        storm: 'There will be a thunderstorm this afternoon.',
        cool: 'It’s cold today. I need a jacket.',
        typhoon: 'A typhoon is coming. Stay at home!',
      },
    },

    kit: {
      title: '🌀 Llista antitifó',
      intro: 'Prepara la casa abans que la tempesta empitjori. Toca cada feina quan l’acabis.',
      calm: 'El nostre kit d’emergència: aigua, una llanterna, piles i una farmaciola. A punt per a tifons i terratrèmols!',
      calmTomorrow: 'Demà arriba un tifó. Demà al matí, prepara la casa amb aquesta llista.',
      done: '✅ Fet',
      go: 'Fer-ho',
      goBalcony: 'Anar al balcó',
      all: 'Tot a punt. Molt bé, expert en tifons!',
      items: {
        // Línies de pràctica d'anglès (els camps `en`).
        water: { icon: '💧', label: 'Omple les ampolles d’aigua i la banyera', en: 'Fill the water bottles.' },
        phone: { icon: '🔋', label: 'Carrega el mòbil i la bateria externa', en: 'Charge the phone.' },
        plants: { icon: '🪴', label: 'Entra les plantes del balcó', en: 'Bring the plants inside.' },
        flashlight: { icon: '🔦', label: 'Busca la llanterna i les piles', en: 'Where is the flashlight?' },
      },
      balconyAsk: 'El vent udola! Els testos podrien sortir volant del balcó. Els entres?',
      balconyYes: '🪴 Entrar-los',
      balconyNo: 'Després',
      balconyDone: 'Els testos són a recer a dins.',
    },

    typhoonMorning: {
      who: 'Mare',
      text: 'Bon dia! El tifó ja és aquí, així que avui és dia lliure per tifó: no hi ha escola ni feina. M’ajudes a preparar la casa? La llista és al kit d’emergència, al costat de la porta.',
      good: '«És clar, mare! Omplo l’aigua i carrego el mòbil.»',
      goodMsg: 'La mare somriu: «El meu petit ajudant!»',
      bad: '«Un dia lliure? Visca, me’n vaig a jugar a fora!»',
      badMsg: 'La mare arrufa les celles: «Ni parlar-ne! A fora és perillós.»',
    },

    quake: {
      who: 'Terratrèmol!',
      text: 'El terra tremola! Les coses trontollen i cauen. Què hauries de fer?',
      drop: '🫣 Ajup-te, cobreix-te el cap i agafa’t',
      outside: '🏃 Sortir corrents al carrer tan ràpid com puguis',
      lift: '🛗 Baixar amb l’ascensor',
      window: '🪟 Posar-te a la finestra a mirar',
      right: 'Correcte! Ajup-te, fica’t sota una taula, cobreix-te el cap i agafa’t fins que pari de tremolar.',
      wrongOutside: 'No és segur! Si corres mentre tremola, pots caure o et poden tocar vidres i rètols que cauen.',
      wrongLift: 'No agafis mai l’ascensor durant un terratrèmol: es pot aturar i deixar-te atrapat. Fes servir les escales quan pari de tremolar.',
      wrongWindow: 'Les finestres es poden trencar! Allunya’t dels vidres.',
      // Línia de pràctica d'anglès.
      phrase: 'Drop, cover and hold on!',
      lesson: 'Digues-ho en anglès: **“Drop, cover and hold on!”** (Ajup-te, cobreix-te i agafa’t!). A Taiwan hi ha molts terratrèmols, així que tothom ho practica a l’escola.',
      after: 'Ha parat de tremolar. Uf… tothom està bé.',
      momWho: 'Mare',
      momHome: 'La mare arriba corrents: «Estàs bé? T’ha caigut res a sobre?»',
      momGood: '«Estic bé, mare! M’he ajupit, m’he cobert i m’he agafat.»',
      momGoodMsg: 'La mare t’abraça fort.',
      momBad: '«No ha estat res, deixa de patir.»',
      momBadMsg: 'La mare sospira, encara tremolosa.',
    },

    umbrella: {
      useRain: '☂️ Obres el paraigua. La pluja hi repica a sobre.',
      useDry: 'Ara no plou. Guardes el paraigua a la motxilla.',
      who: 'Paraiguer',
      ask: (price: number) => `Una galleda de paraigües al costat de la porta, com a totes les botigues de conveniència de Taiwan. Un costa ${price} monedes.`,
      buy: (price: number) => `☂️ Comprar-ne un (${price}🪙)`,
      no: 'No, gràcies',
      poor: 'No tens prou monedes…',
      bought: '☂️ Ja tens paraigua! La pluja ja no t’atura.',
      have: 'Ja portes un paraigua a la motxilla.',
    },

    calls: {
      momTyphoon: {
        caller: 'Mare ❤️',
        desc: 'Amoïnada pel tifó',
        intro: 'Bonic, el tifó s’està acostant! On ets? Torna a casa abans que el vent bufi fort, i no t’acostis al riu.',
        good: '«Ja torno a casa, mare. T’ajudo a preparar-ho tot!»',
        goodMsg: 'La mare sembla alleujada: «Gràcies, carinyo. Vés amb compte!»',
        bad: '«Només és una mica de vent. Em quedo a fora.»',
        badMsg: 'A la mare se li tensa la veu de preocupació…',
      },
      momQuake: {
        caller: 'Mare ❤️',
        desc: 'Truca just després del terratrèmol',
        intro: 'Has notat el terratrèmol? T’has fet mal? On ets ara mateix?',
        good: '«Estic bé, mare! M’he ajupit, m’he cobert i m’he agafat.»',
        goodMsg: 'La mare torna a respirar: «Molt bé! És exactament el que s’ha de fer.»',
        bad: '«Uf, mare, no ha estat res.»',
        badMsg: 'La mare diu en veu baixa: «És que he patit per tu…»',
      },
      momRain: {
        caller: 'Mare ❤️',
        desc: 'Està plovent',
        intro: 'Està plovent! Has agafat el paraigua? No et mullis, que et refredaràs.',
        good: '«Gràcies, mare! Compraré un paraigua a la botiga.»',
        goodMsg: 'La mare: «Bon nen. Eixuga’t els cabells quan arribis a casa!»',
        bad: '«La pluja m’és igual.»',
        badMsg: 'La mare sospira: «Doncs després no tornis a casa esternudant…»',
      },
    },

    journal: {
      typhoonReady: 'Vaig preparar la casa per a un tifó',
      quakeSafe: 'Em vaig protegir en un terratrèmol: ajup-te, cobreix-te i agafa’t!',
    },
  },
};
